import React from 'react';
import { Link } from 'react-router-dom';
import { 
  AlertTriangle, 
  CreditCard, 
  Mail, 
  Download, 
  LogIn, 
  Keyboard,
  ShieldCheck,
  RefreshCcw,
  UserPlus,
  Info,
  ChevronRight,
  ArrowUpRight,
  BookOpen,
  Scale
} from 'lucide-react';
import UserNavbar from '../components/UserNavbar';
import Footer from '../components/Footer';
import config from '../../config/env';

const MustRead = () => {
  const usageSteps = [
    {
      icon: UserPlus,
      num: '01',
      title: 'Create an Account',
      description: 'Sign up with a username and password. Remember your password carefully as there is no password reset option available.',
    },
    {
      icon: LogIn,
      num: '02',
      title: 'Login to Your Account',
      description: 'Login using your credentials on the website to access your dashboard and purchase learning credits.',
    },
    {
      icon: CreditCard,
      num: '03',
      title: 'Purchase Learning Credits',
      description: 'Buy credits using Razorpay payment gateway. Credits are used for AI-powered learning assistance.',
    },
    {
      icon: Download,
      num: '04',
      title: 'Download the Extension',
      description: 'Download the NeoPass Learning browser extension from our website and install it in Chrome, Edge, or Brave.',
    },
    {
      icon: LogIn,
      num: '05',
      title: 'Login in Extension',
      description: 'Open the extension popup and login with the same credentials you used on the website.',
    },
    {
      icon: Keyboard,
      num: '06',
      title: 'Use Keyboard Shortcuts',
      description: 'Use the keyboard shortcuts below to access the learning assistant conveniently while studying.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#111111]">
      <UserNavbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <span className="text-white/40 text-xs font-mono tracking-widest mb-4 block">LEGAL</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6 tracking-tight">
            Terms of Service
          </h1>
          <p className="text-white/60 text-lg max-w-2xl">
            Please read the following terms and policies carefully before using NeoPass Learning platform.
          </p>
        </div>
      </section>

      {/* Policies Section */}
      <section className="py-12 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto grid gap-px bg-white/10">
          
          {/* Account Security */}
          <div id="terms" className="bg-[#111111] p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <ShieldCheck className="h-5 w-5 text-accent" />
              <h2 className="text-xl font-medium text-white">Account Security</h2>
            </div>
            <p className="text-white/70 mb-6 leading-relaxed">
              NeoPass Learning uses a secure authentication system. For security reasons, <span className="text-accent">password reset functionality is not available.</span> Please store your credentials safely.
            </p>
            <p className="text-white/70 mb-6 leading-relaxed">
              Please remember your password carefully before purchasing any credits. If you forget your password, you will need to create a new account.
            </p>
            <div className="text-white/50 text-sm">
              <p className="mb-3 text-white/70">We strongly recommend:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Write down your password in a safe place</li>
                <li>Use a password manager to store your credentials</li>
                <li>Choose a password you will easily remember</li>
                <li>Verify your account access before making any purchase</li>
              </ul>
            </div>
          </div>

          {/* Refund Policy */}
          <div id="refund-policy" className="bg-[#111111] p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <RefreshCcw className="h-5 w-5 text-accent" />
              <h2 className="text-xl font-medium text-white">Refund & Cancellation Policy</h2>
            </div>
            <div className="border border-red-500/30 bg-red-500/5 p-4 rounded mb-6">
              <p className="text-red-400 font-medium mb-2">All Sales Are Final</p>
              <p className="text-white/60 text-sm">
                All purchases of NeoPass Learning credits are final and non-refundable. Due to the instant digital delivery of credits, we do not offer refunds, returns, or cancellations.
              </p>
            </div>
            <div className="text-white/50 text-sm mb-6">
              <p className="mb-3 text-white/70">Please note:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Credits are delivered instantly upon successful payment</li>
                <li>No refunds are provided once credits are added to your account</li>
                <li>Start with a smaller pack to test the service before larger purchases</li>
                <li>Review our Terms of Service before purchasing</li>
              </ul>
            </div>
            <p className="text-white/50 text-sm">
              For technical issues only, contact <a href="mailto:neopassbusiness@gmail.com" className="text-accent hover:text-accent-light">neopassbusiness@gmail.com</a>. See our full <Link to="/refund-policy" className="text-accent hover:text-accent-light">Refund Policy</Link>.
            </p>
          </div>

          {/* About Service */}
          <div className="bg-[#111111] p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <Info className="h-5 w-5 text-accent" />
              <h2 className="text-xl font-medium text-white">About Our Service</h2>
            </div>
            <p className="text-white/70 mb-6 leading-relaxed">
              NeoPass Learning is an AI-powered educational browser extension that provides personalized learning assistance for students studying for exams, homework, and self-study.
            </p>
            <div className="text-white/50 text-sm">
              <p className="mb-3 text-white/70">What you get with NeoPass Learning:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>AI-powered concept explanations using Gen AI technology</li>
                <li>Browser extension compatible with Chrome, Edge, and Brave</li>
                <li>Keyboard shortcuts for quick access during study sessions</li>
                <li>Step-by-step problem solving assistance</li>
                <li>Credit-based usage system with transparent pricing</li>
              </ul>
            </div>
          </div>

          {/* Fair Use Policy */}
          <div className="bg-[#111111] p-8 md:p-10">
            <div className="flex items-center gap-3 mb-6">
              <Scale className="h-5 w-5 text-accent" />
              <h2 className="text-xl font-medium text-white">Fair Use Policy</h2>
            </div>
            <p className="text-white/70 mb-6 leading-relaxed">
              NeoPass Learning is designed exclusively for self-study, homework assistance, and exam preparation. Our platform is intended to help students understand concepts and master subjects through AI-powered tutoring.
            </p>
            <div className="border border-accent\/30 bg-accent\/5 p-4 rounded mb-6">
              <p className="text-accent\/90 text-sm font-medium">
                This tool is not designed to be used during live proctored examinations.
              </p>
            </div>
            <p className="text-white/50 text-sm">
              By using our service, you agree to use it responsibly for educational purposes only. Misuse of the platform may result in account suspension.
            </p>
          </div>
        </div>
      </section>

      {/* How to Use Section */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <span className="text-white/40 text-xs font-mono tracking-widest mb-4 block">GETTING STARTED</span>
          <h2 className="text-3xl md:text-4xl font-medium text-white mb-4">How to Use NeoPass Learning</h2>
          <p className="text-white/50 mb-12 max-w-2xl">
            Follow these steps to get started with NeoPass Learning and make the most of your study sessions.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {usageSteps.map((item) => (
              <div key={item.num} className="bg-[#0a0a0a] p-8 group">
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-2xl font-light text-white/20 group-hover:text-accent/50 transition-colors font-mono">
                    {item.num}
                  </span>
                  <item.icon className="h-5 w-5 text-accent" />
                </div>
                <h3 className="text-lg font-medium text-white mb-3 group-hover:text-accent transition-colors">
                  {item.title}
                </h3>
                <p className="text-white/50 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Keyboard Shortcuts Section */}
      <section className="py-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-white/40 text-xs font-mono tracking-widest mb-4 block">PRODUCTIVITY</span>
              <h2 className="text-3xl md:text-4xl font-medium text-white mb-6">
                Keyboard Shortcuts
              </h2>
              <p className="text-white/50">
                Master these shortcuts to use NeoPass Learning efficiently during your study sessions.
              </p>
            </div>

            <div className="space-y-px">
              {config.shortcuts.map((shortcut, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-5 bg-white/[0.02] border border-white/10 hover:border-white/20 transition-colors group"
                >
                  <div className="flex items-center gap-2">
                    {shortcut.keys.map((key, i) => (
                      <React.Fragment key={i}>
                        <kbd className="px-3 py-1.5 text-xs font-mono bg-white/5 text-white/70 border border-white/10 group-hover:border-white/20 transition-colors">
                          {key}
                        </kbd>
                        {i < shortcut.keys.length - 1 && (
                          <span className="text-white/20">+</span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                  <span className="text-white/60 text-sm font-medium">
                    {shortcut.action}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-xl">
            <span className="text-white/40 text-xs font-mono tracking-widest mb-4 block">SUPPORT</span>
            <h2 className="text-3xl font-medium text-white mb-6">Contact Information</h2>
            <p className="text-white/50 mb-8">
              If you have any questions, concerns, or need assistance, please feel free to contact us:
            </p>
            <div className="p-6 border border-white/10 bg-white/[0.02]">
              <div className="flex items-center gap-4">
                <Mail className="h-5 w-5 text-accent" />
                <div>
                  <p className="text-white/40 text-xs font-mono tracking-widest mb-1">EMAIL</p>
                  <a 
                    href="mailto:neopassbusiness@gmail.com" 
                    className="text-white hover:text-accent transition-colors"
                  >
                    neopassbusiness@gmail.com
                  </a>
                </div>
              </div>
            </div>
            <p className="mt-6 text-sm text-white/40">
              We typically respond to emails within 24-48 hours. For urgent issues, please include "URGENT" in your email subject line.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-medium text-white mb-6">
            Ready to Start Learning?
          </h2>
          <p className="text-white/50 mb-10 max-w-xl mx-auto">
            By proceeding, you acknowledge that you have read and understood our terms and policies above.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/signup"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black font-medium hover:bg-white/90 transition-colors"
            >
              Create Account
              <ChevronRight className="h-4 w-4" />
            </Link>
            <Link 
              to="/pricing"
              className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white font-medium hover:bg-white/5 transition-colors"
            >
              View Credit Packs
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer variant="full" />
    </div>
  );
};

export default MustRead;
